import {Component, computed, Signal, signal, ViewChild, WritableSignal} from "@angular/core";
import {PopoverComponent} from "@fundamental-ngx/core/popover";
import {NavigationService} from "../../../../../service/services/misc/navigation/navigation.service";
import {Const} from "../../../../helpers/Const";
import {Subject} from "rxjs";
import {RecentItemModel} from "./recent/recent-item.model";
import {RecentListComponent} from "./recent/recent-list.component";
import {
    OrganizationHierarchyStoreService
} from "../../../../../service/store/organization-hierarchy-store/organization-hierarchy-store.service";

@Component({
    selector: 'app-go-to-path',
    templateUrl: './go-to-path.component.html',
    styleUrls: ['./go-to-path.component.scss'],
    standalone: false
})
export class GoToPathComponent {
    constructor(
        private readonly _organizationHierarchyStore: OrganizationHierarchyStoreService,
        private readonly _navigationService: NavigationService) {
    }

    isGoToPathPopoverOpen = false;

    @ViewChild('goToPathPopover')
        popoverComponent: PopoverComponent;

    @ViewChild('recentListComponent')
        recentListComponent: RecentListComponent;

    readonly pathSignal : WritableSignal<string> = signal('');
    readonly trimmedPath: Signal<string> = computed(() => (this.pathSignal() ?? '').trim());

    pathEnteredEvent: Subject<string> = new Subject<string>();

    error: string;
    busy = false;

    emitPathEnteredEvent(path: string) {
        this.pathEnteredEvent.next(path);
    }

    async onRecentItemClicked(recentItem: RecentItemModel) {
        await this.go(recentItem.path);
    }

    onOpenChange() {
        this.pathSignal.set('');
        this.error = "";
        this.recentListComponent.resetListComponent();
    }

    async go(path: string): Promise<void> {
        const cleanedPathSegments = this._getPathSegmentsArray(path);
        const organizationName = cleanedPathSegments.shift(); //First part of input path is org name
        const errorMessage = "Resource does not exist, or you don't have permissions to access it.";

        try {
            // Validate that the user has permissions to org
            await this._organizationHierarchyStore.getOrFetchOrganization(organizationName);
        } catch {
            this.error = errorMessage;
            return;
        }

        if (cleanedPathSegments.length === 0) {
            void this._navigationService.goToResource(Const.ALL_PROJECTS_PARAM, organizationName, undefined, undefined);
            this.emitPathEnteredEvent(path);
            this.popoverComponent.close();
            return;
        }
        const resourceName: string | undefined = cleanedPathSegments.pop();
        this.busy = true;
        try {
            await this._navigationService.navigateToResourceByPath(
                cleanedPathSegments,
                organizationName,
                resourceName,
                () => {
                    this.emitPathEnteredEvent(path);
                    this.popoverComponent.close();
                }
            );
        } catch (e: any) {
            // Handle non-container resources by navigating to parent
            if (e.message.startsWith("Resource is not a container")) {
                const parentPath = e.message.split("Parent path: ")[1];
                return await this.go(parentPath);
            }
            this.error = errorMessage;
        } finally {
            this.busy = false;
        }
    }

    onCancel() {
        this.popoverComponent.close();
    }

    _getPathSegmentsArray(path: string): string[] {
        const pathSegments: string[] = path.split('/');
        return pathSegments.filter(segment => segment !== '');
    }
}
